import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowRight, CheckCircle2, ClipboardList, FileText, LifeBuoy, Plus, CalendarDays, AlertCircle } from 'lucide-react';
import { displayNumber, formatDate, jobStage } from '../../utils/jobPresentation';

// All client surfaces use theme.css. No page-specific colors.
const PANEL = 'cl-panel rounded-xl';

function OrderRow({ job }) {
  const step = jobStage(job);
  return (
    <article className="cl-order-row px-5 sm:px-6 py-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="cl-label">{displayNumber(job)}</span>
            <span className="cl-status">{step.label}</span>
          </div>
          <h3 className="mt-2 font-semibold truncate">{job.serviceType || job.title || 'IT service request'}</h3>
          <p className="mt-1 text-sm">{step.description}</p>
        </div>
        <Link to={`/jobs/${job.id}`} className="cl-btn-primary shrink-0" aria-label={`${step.action || 'View details'} for ${displayNumber(job)}`}>
          {step.action || 'View details'} <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}

export default function Dashboard() {
  const { currentUser, userProfile } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!currentUser?.uid) { setLoading(false); setJobs([]); return; }
    let active = true;
    setLoading(true);
    setError('');
    const q = query(collection(db, 'workOrders'), where('clientId', '==', currentUser.uid), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, snapshot => {
      if (active) { setJobs(snapshot.docs.map(row => ({ id: row.id, ...row.data() }))); setLoading(false); }
    }, err => {
      console.error('Client dashboard query failed', err);
      if (active) { setError('We could not load your service requests. Please refresh the page.'); setLoading(false); }
    });
    return () => { active = false; unsubscribe(); };
  }, [currentUser?.uid]);

  const groups = useMemo(() => {
    const sorted = { attention: [], active: [], closed: [] };
    jobs.forEach(job => sorted[jobStage(job).group].push(job));
    return sorted;
  }, [jobs]);
  const firstName = (userProfile?.contactName || userProfile?.name || 'there').trim().split(/\s+/)[0];
  const next = groups.attention[0];
  const scheduled = groups.active.filter(job => job.status === 'scheduled').sort((a, b) => String(a.schedule?.date || '').localeCompare(String(b.schedule?.date || '')))[0];
  const summary = [
    { label: 'Action needed', value: groups.attention.length, icon: AlertCircle },
    { label: 'Active services', value: groups.active.length, icon: ClipboardList },
    { label: 'Completed', value: groups.closed.filter(j => j.status === 'completed').length, icon: CheckCircle2 },
  ];

  return (
    <div className="cl-page cl-dashboard min-h-[calc(100vh-76px)]">
      <div className="max-w-[1120px] mx-auto px-4 sm:px-7 lg:px-10 py-8 sm:py-10 space-y-7">
        <header className="cl-dashboard-hero rounded-2xl px-6 sm:px-8 py-7 sm:py-9 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <p className="cl-overline">Your workspace</p>
            <h1 className="mt-2 text-3xl sm:text-[34px] font-semibold tracking-tight">Welcome back, {firstName}.</h1>
            <p className="mt-2 text-base max-w-lg">Manage your service requests, appointments and documents from one place.</p>
          </div>
          <Link to="/book-service" className="cl-btn-primary shrink-0"><Plus className="w-4 h-4" />Request service</Link>
        </header>

        {error && <div className="cl-alert p-5" role="alert">{error}</div>}
        {!error && <>
          <section className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" aria-label="Service summary">
            {summary.map(({ label, value, icon: Icon }) => (
              <div key={label} className={`${PANEL} p-5 flex items-center gap-4`}>
                <span className="cl-icon-tile h-12 w-12 shrink-0 rounded-lg grid place-items-center"><Icon className="w-5 h-5" aria-hidden="true" /></span>
                <div><div className="text-2xl sm:text-3xl font-semibold tabular-nums">{loading ? '—' : value}</div><div className="text-sm mt-1">{label}</div></div>
              </div>
            ))}
          </section>

          <section aria-labelledby="attention-heading">
            <div className="flex items-center justify-between gap-4 mb-4">
              <h2 id="attention-heading" className="text-lg font-semibold tracking-tight">Needs your attention</h2>
              {groups.attention.length > 1 && <Link to="/jobs" className="cl-text-link">View all <ArrowRight className="inline w-4 h-4" /></Link>}
            </div>
            {loading ? <div className={`${PANEL} p-7`}>Loading…</div> : next ?
              <div className="cl-attention rounded-xl p-5 sm:p-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
                <div><div className="cl-overline">Action required · {displayNumber(next)}</div><h3 className="mt-2 text-lg font-semibold">{jobStage(next).label}</h3><p className="mt-1 text-sm">{next.serviceType || next.title || 'IT service'} — {jobStage(next).description}</p></div>
                <Link to={`/jobs/${next.id}`} className="cl-btn-primary">{jobStage(next).action} <ArrowRight className="w-4 h-4" /></Link>
              </div> :
              <div className={`${PANEL} p-6 flex items-start gap-3`}><CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" aria-hidden="true" /><div><strong className="text-sm">You're all caught up.</strong><p className="text-sm mt-1">There are no approvals or payments awaiting your action.</p></div></div>}
          </section>

          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5">
            <section aria-labelledby="requests-heading" className={`${PANEL} overflow-hidden`}>
              <div className="px-5 sm:px-6 py-5 border-b cl-divider flex items-center justify-between gap-3">
                <h2 id="requests-heading" className="text-lg font-semibold">Your service requests</h2>
                <Link to="/jobs" className="cl-text-link">View all <ArrowRight className="inline w-4 h-4" /></Link>
              </div>
              {loading ? <div className="p-6">Loading your requests…</div> : jobs.length ?
                <div className="cl-dividers divide-y">{[...groups.attention, ...groups.active, ...groups.closed].slice(0, 4).map(job => <OrderRow key={job.id} job={job} />)}</div> :
                <div className="p-7"><p className="font-semibold">No service requests yet.</p><p className="mt-2 text-sm">Submit a request and we'll send you a written quote.</p><Link to="/book-service" className="cl-text-link inline-flex items-center gap-2 mt-5">Request your first service <ArrowRight className="w-4 h-4" /></Link></div>}
            </section>
            <div className="space-y-5">
              {scheduled && <section className={`${PANEL} p-6`}>
                <div className="flex items-center gap-2"><CalendarDays className="w-5 h-5" /><h2 className="font-semibold">Upcoming appointment</h2></div>
                <p className="mt-4 text-sm font-semibold">{scheduled.serviceType || scheduled.title}</p>
                <p className="mt-1 text-sm">{formatDate(scheduled.schedule.date)} · {scheduled.schedule.startTime || 'Time to be confirmed'}</p>
                <Link to={`/jobs/${scheduled.id}`} className="cl-text-link mt-4 inline-flex gap-1">Appointment details <ArrowRight className="w-4 h-4" /></Link>
              </section>}
              <section className={`${PANEL} p-6`}><h2 className="font-semibold">Quick access</h2><div className="cl-dividers mt-4 divide-y">
                <Link to="/documents" className="cl-simple-row flex items-center gap-3 py-3 text-sm font-medium"><FileText className="w-5 h-5" />Service reports & documents <ArrowRight className="w-4 h-4 ml-auto" /></Link>
                <Link to="/support" className="cl-simple-row flex items-center gap-3 py-3 text-sm font-medium"><LifeBuoy className="w-5 h-5" />Contact support <ArrowRight className="w-4 h-4 ml-auto" /></Link>
              </div></section>
            </div>
          </div>
        </>}
      </div>
    </div>
  );
}
